// WC-V20-E: Bell Notifications — workflow only. Team chat opens Firefly Messages
// (single Stream host). Dev sync: remove thin bell embed.
import { useState } from "react";
import type { UseWorkplaceMessagesStateResult } from "@/components/messages/useWorkplaceMessagesState";
import { useWorkplaceChatVendorState } from "@/components/messages/useWorkplaceChatVendorState";
import { WorkplaceMessagesPanel } from "@/components/messages/WorkplaceMessagesPanel";
import { WorkplaceRemindersSection } from "@/components/messages/WorkplaceRemindersSection";
import { WorkplaceWorkflowNotificationsPanel } from "@/components/messages/WorkplaceWorkflowNotificationsPanel";
import { WorkplaceThreadDetail } from "@/components/messages/WorkplaceThreadDetail";
import { WorkplaceActionableAcksPanel } from "@/components/messages/WorkplaceActionableAcksPanel";
import { openFireflyActivityDock } from "@/components/activity/activityDockOpenEvent";
import { isWorkplaceLayerBDmKind } from "@/lib/messages/workplaceLayerModel";
import { listOpsNotifications, markOpsNotificationsReadByIds } from "@/lib/opsPaperworkClient";
import { CHAT_ACTIVITY_BRIDGE_KIND } from "@/lib/messages/chatActivityBridgeModel";

export type WorkplaceActivityNotificationsSectionProps = {
  wpState: UseWorkplaceMessagesStateResult;
  allowTestNotification: boolean;
  allowSenderLock: boolean;
  allowRequiresAck: boolean;
  panelOpen: boolean;
  /** Close the bell when jumping to Firefly Messages. */
  onClosePanel?: () => void;
};

export function WorkplaceActivityNotificationsSection({
  wpState,
  allowTestNotification,
  allowSenderLock,
  allowRequiresAck,
  panelOpen,
  onClosePanel,
}: WorkplaceActivityNotificationsSectionProps) {
  const vendorState = useWorkplaceChatVendorState(panelOpen);
  const vendorMode = vendorState.vendorEnabled === true;
  const [ackEpoch, setAckEpoch] = useState(0);

  const bumpAcks = () => {
    setAckEpoch((n) => n + 1);
    void wpState.refreshSummary();
  };

  const workflowSelectedThread =
    wpState.selectedThreadId &&
    wpState.threads.find((t) => t.thread_id === wpState.selectedThreadId) &&
    !isWorkplaceLayerBDmKind(
      String(wpState.threads.find((t) => t.thread_id === wpState.selectedThreadId)?.latest_message?.kind || ""),
    );

  function openTeamChatInFirefly() {
    void (async () => {
      const n = await listOpsNotifications();
      if (n.ok && n.data && typeof n.data === "object" && "notifications" in n.data) {
        const rows = (n.data as { notifications?: Array<{ id?: string; kind?: string; read_at?: string | null }> })
          .notifications;
        const ids = (rows ?? [])
          .filter((row) => row.kind === CHAT_ACTIVITY_BRIDGE_KIND && !row.read_at && row.id)
          .map((row) => String(row.id));
        if (ids.length) {
          await markOpsNotificationsReadByIds(ids);
          await wpState.refreshSummary();
        }
      }
    })();
    void openFireflyActivityDock({ force: true, focusSection: "messages" });
    onClosePanel?.();
  }

  return (
    <div className="border-t border-gray-100 dark:border-gray-800">
      <div className="px-4 pt-3 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Notifications
      </div>
      <div className="space-y-3 px-4 pb-3 pt-1">
        <WorkplaceActionableAcksPanel
          key={ackEpoch}
          enabled={panelOpen}
          onAcknowledged={bumpAcks}
        />
        <WorkplaceRemindersSection
          surface="bell"
          compact
          reassignMembers={wpState.members.map((m) => ({
            user_id: m.user_id,
            label: m.name || m.email || m.user_id.slice(0, 8),
          }))}
          navigation={{
            onOpenThread: (threadId) => wpState.onSelectThread(threadId),
          }}
          onChanged={bumpAcks}
        />

        {vendorMode ? (
          <button
            type="button"
            onClick={openTeamChatInFirefly}
            className="w-full rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-left text-xs font-medium text-teal-900 hover:bg-teal-100 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-100 dark:hover:bg-teal-950/70"
          >
            Open team chat
            <span className="mt-0.5 block text-[10px] font-normal text-teal-800/80 dark:text-teal-200/80">
              Opens Firefly Messages (same chat as the dock).
            </span>
          </button>
        ) : null}

        {vendorMode ? (
          <div>
            <WorkplaceWorkflowNotificationsPanel
              threads={wpState.threads}
              loadingList={wpState.loadingList}
              allowTestNotification={allowTestNotification}
              testBusy={wpState.testBusy}
              onTest={wpState.onTest}
              onSelectThread={wpState.onSelectThread}
              selectedThreadId={wpState.selectedThreadId}
            />
            {workflowSelectedThread ? (
              <div className="mt-2">
                <WorkplaceThreadDetail
                  p={{ ...wpStateToPanelProps(wpState, allowTestNotification, allowSenderLock, allowRequiresAck) }}
                />
              </div>
            ) : null}
          </div>
        ) : (
          <WorkplaceMessagesPanel
            {...wpStateToPanelProps(wpState, allowTestNotification, allowSenderLock, allowRequiresAck)}
          />
        )}
      </div>
    </div>
  );
}

function wpStateToPanelProps(
  wpState: UseWorkplaceMessagesStateResult,
  allowTestNotification: boolean,
  allowSenderLock: boolean,
  allowRequiresAck: boolean,
) {
  return {
    allowTestNotification,
    allowSenderLock,
    allowRequiresAck,
    errorText: wpState.errorText,
    unreadCount: wpState.unreadCount,
    unackedCount: wpState.unackedCount,
    testBusy: wpState.testBusy,
    markBusy: wpState.markBusy,
    replyBusy: wpState.replyBusy,
    archiveBusy: wpState.archiveBusy,
    loadingList: wpState.loadingList,
    threads: wpState.threads,
    selectedThreadId: wpState.selectedThreadId,
    messages: wpState.messages,
    participantReceipts: wpState.participantReceipts,
    members: wpState.members,
    viewer: wpState.viewer,
    recipientId: wpState.recipientId,
    setRecipientId: wpState.setRecipientId,
    newThreadTitle: wpState.newThreadTitle,
    setNewThreadTitle: wpState.setNewThreadTitle,
    newThreadText: wpState.newThreadText,
    setNewThreadText: wpState.setNewThreadText,
    newThreadSenderLocked: wpState.newThreadSenderLocked,
    setNewThreadSenderLocked: wpState.setNewThreadSenderLocked,
    newThreadRequiresAck: wpState.newThreadRequiresAck,
    setNewThreadRequiresAck: wpState.setNewThreadRequiresAck,
    newThreadAckDeadline: wpState.newThreadAckDeadline,
    setNewThreadAckDeadline: wpState.setNewThreadAckDeadline,
    replyText: wpState.replyText,
    setReplyText: wpState.setReplyText,
    replyRequiresAck: wpState.replyRequiresAck,
    setReplyRequiresAck: wpState.setReplyRequiresAck,
    onMarkAll: wpState.onMarkAll,
    onTest: wpState.onTest,
    onCreateThread: wpState.onCreateThread,
    onSendReply: wpState.onSendReply,
    onSelectThread: wpState.onSelectThread,
    onMarkOne: wpState.onMarkOne,
    onAcknowledge: wpState.onAcknowledge,
    onArchiveOne: wpState.onArchiveOne,
    onArchiveThread: wpState.onArchiveThread,
  };
}
